import type { AccordionItem, AccordionProps } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { CheckboxField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

function newItemId(): string {
  return `a-${Math.random().toString(36).slice(2, 8)}`;
}

export function AccordionInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as AccordionProps;
  const items = p.items ?? [];

  const setItems = (next: AccordionItem[]) => update(widget.id, { items: next });

  const addItem = () =>
    setItems([...items, { id: newItemId(), title: "New question", body: "" }]);

  const updateItem = (id: string, patch: Partial<AccordionItem>) =>
    setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const removeItem = (id: string) => setItems(items.filter((it) => it.id !== id));

  const moveItem = (id: string, dir: "up" | "down") => {
    const idx = items.findIndex((it) => it.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setItems(next);
  };

  return (
    <>
      <CheckboxField
        label="Only one item open at a time"
        value={p.exclusive ?? true}
        onChange={(v) => update(widget.id, { exclusive: v })}
      />
      <CheckboxField
        label="Open the first item by default"
        value={p.defaultOpenFirst ?? false}
        onChange={(v) => update(widget.id, { defaultOpenFirst: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Items ({items.length})
      </div>

      {items.map((it, i) => (
        <div
          key={it.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "up")}
              disabled={i === 0}
              title="Move up"
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "down")}
              disabled={i === items.length - 1}
              title="Move down"
              style={{
                padding: "2px 6px",
                opacity: i === items.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeItem(it.id)}
              title="Delete item"
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>
          <TextField
            label={`Q${i + 1} title`}
            value={it.title}
            onChange={(v) => updateItem(it.id, { title: v })}
          />
          <TextField
            label="Body"
            value={it.body}
            onChange={(v) => updateItem(it.id, { body: v })}
            multiline
          />
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addItem}
          style={{ width: "100%" }}
        >
          + Add item
        </button>
      </div>
    </>
  );
}
